/**
 * 契約者ログイン（ARCHITECTURE.md §7.1）。
 *
 * 2 段階:
 *   1. メールアドレス + パスワード → `mfaRequired` なら mfaTicket を受け取る
 *   2. TOTP もしくはリカバリコード → access token
 *
 * mfaTicket は Redis 上で 5 分・IP/UA 固定・5 回失敗でロック。したがって
 * 画面側でも「やり直す」導線を必ず出す（チケット切れで詰まると問い合わせになる）。
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from '@mail-sv/ui';
import { Link } from '@tanstack/react-router';
import { Mail, ShieldCheck } from 'lucide-react';
import { forgotPassword, login, loginMfa } from '../lib/auth.js';
import { isMfaChallenge } from '../lib/api-types.js';
import { ApiError, errorMessage } from '../lib/api.js';
import { useAppNavigate, useSearchParams } from '../lib/nav.js';
import { useSubmitOnce } from '../lib/submit-once.js';
import { DevLoginButton } from '../components/dev-login-button.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { env } from '../env.js';

type Stage =
  { kind: 'credentials' } | { kind: 'mfa'; ticket: string; methods: Array<'totp' | 'recovery'> } | { kind: 'forgot' };

export function LoginPage() {
  const navigate = useAppNavigate();
  const search = useSearchParams();
  const [stage, setStage] = React.useState<Stage>({ kind: 'credentials' });

  const goHome = React.useCallback(() => {
    const to = typeof search.redirect === 'string' && search.redirect.startsWith('/') ? search.redirect : '/';
    navigate({ to, replace: true });
  }, [navigate, search.redirect]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <div className="brand-gradient mx-auto flex size-12 items-center justify-center rounded-xl text-white shadow-lg shadow-primary/25">
            <Mail className="size-6" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold">{env.brandName} コントロールパネル</h1>
          <p className="text-sm text-muted-foreground">メールアドレスとパスワードでログインしてください。</p>
        </div>

        <Card>
          {stage.kind === 'credentials' ? (
            <CredentialsForm
              onMfaRequired={(ticket, methods) => setStage({ kind: 'mfa', ticket, methods })}
              onSuccess={goHome}
              onForgot={() => setStage({ kind: 'forgot' })}
            />
          ) : stage.kind === 'mfa' ? (
            <MfaForm
              ticket={stage.ticket}
              methods={stage.methods}
              onSuccess={goHome}
              onRestart={() => setStage({ kind: 'credentials' })}
            />
          ) : (
            <ForgotForm onBack={() => setStage({ kind: 'credentials' })} />
          )}
        </Card>

        <DevLoginButton onSuccess={goHome} />

        {/* 申込導線。規約は**ログイン前に読めなければ意味が無い**ので併記する。 */}
        <p className="text-center text-xs text-muted-foreground">
          <Link to="/signup" className="text-primary underline-offset-4 hover:underline">
            新規のお申し込み
          </Link>
          {' / '}
          <Link to="/legal" className="text-primary underline-offset-4 hover:underline">
            規約・法令表示
          </Link>
        </p>

        {env.supportUrl ? (
          <p className="text-center text-xs text-muted-foreground">
            ログインできない場合は{' '}
            <a
              className="text-primary underline-offset-4 hover:underline"
              href={env.supportUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              サポート
            </a>
            までお問い合わせください。
          </p>
        ) : null}
      </div>
    </div>
  );
}

function CredentialsForm({
  onMfaRequired,
  onSuccess,
  onForgot,
}: {
  onMfaRequired: (ticket: string, methods: Array<'totp' | 'recovery'>) => void;
  onSuccess: () => void;
  onForgot: () => void;
}) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  // 二重送信の掛け金。再送は**レート制限の枠を焼く**（429 で正規の利用者が締め出される）。
  const gate = useSubmitOnce();

  const mut = useMutation({
    mutationFn: () => login(email.trim(), password),
    onError: gate.release,
    onSuccess: (res) => {
      if (isMfaChallenge(res)) {
        onMfaRequired(res.mfaTicket, res.methods);
        return;
      }
      onSuccess();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (gate.claim()) mut.mutate();
      }}
    >
      <CardHeader>
        <CardTitle>ログイン</CardTitle>
        <CardDescription>ご契約時に登録したメールアドレスを入力してください。</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormAlert error={mut.error} />
        <Field label="メールアドレス" htmlFor="login-email" required>
          <Input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </Field>
        <Field label="パスワード" htmlFor="login-password" required>
          <Input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>
        <SubmitBar
          submitLabel="ログイン"
          pending={mut.isPending}
          disabled={email.trim().length === 0 || password.length === 0}
        />
        <button
          type="button"
          className="w-full text-center text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={onForgot}
        >
          パスワードをお忘れの方
        </button>
      </CardContent>
    </form>
  );
}

/** 2 段目のフォーム。**回帰テストのために export している**（`login.test.tsx`）。 */
export function MfaForm({
  ticket,
  methods,
  onSuccess,
  onRestart,
}: {
  ticket: string;
  methods: Array<'totp' | 'recovery'>;
  onSuccess: () => void;
  onRestart: () => void;
}) {
  const [method, setMethod] = React.useState<'totp' | 'recovery'>(methods[0] ?? 'totp');
  const [code, setCode] = React.useState('');

  const gate = useSubmitOnce();

  const mut = useMutation({
    mutationFn: () => loginMfa(ticket, method, code.trim()),
    // **成功しても解放しない。** `mfaTicket` は 1 回きりで、再送は必ず
    // `MFA_TICKET_INVALID` になる（詳しい経緯は `lib/submit-once.ts`）。
    onError: gate.release,
    onSuccess,
  });

  // チケット切れは「やり直す」以外に復帰手段が無いので、文言で明示的に誘導する。
  const ticketExpired = mut.error instanceof ApiError && mut.error.is('MFA_TICKET_INVALID');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (gate.claim()) mut.mutate();
      }}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="size-5 text-primary" aria-hidden />2 段階認証
        </CardTitle>
        <CardDescription>
          {method === 'totp'
            ? '認証アプリに表示されている 6 桁の数字を入力してください。'
            : 'バックアップ用のリカバリコードを入力してください。'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {mut.isError ? (
          <Alert variant="destructive">
            <AlertTitle>確認できませんでした</AlertTitle>
            <AlertDescription>
              {errorMessage(mut.error)}
              {ticketExpired ? '「最初からやり直す」を押してログインし直してください。' : ''}
            </AlertDescription>
          </Alert>
        ) : null}

        <Field label={method === 'totp' ? '認証コード（6 桁）' : 'リカバリコード'} htmlFor="mfa-code" required>
          <Input
            id="mfa-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode={method === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            autoFocus
            className="font-mono tracking-widest"
            required
          />
        </Field>

        <SubmitBar submitLabel="ログイン" pending={mut.isPending} disabled={code.trim().length === 0} />

        <div className="flex items-center justify-between gap-2 text-xs">
          {methods.includes('recovery') ? (
            <button
              type="button"
              className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => {
                setMethod((m) => (m === 'totp' ? 'recovery' : 'totp'));
                setCode('');
              }}
            >
              {method === 'totp' ? 'リカバリコードを使う' : '認証アプリのコードを使う'}
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            onClick={onRestart}
          >
            最初からやり直す
          </button>
        </div>
      </CardContent>
    </form>
  );
}

function ForgotForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = React.useState('');
  // 二重送信すると再設定メールが 2 通届く。利用者はどちらが有効か分からない。
  const gate = useSubmitOnce();
  const mut = useMutation({ mutationFn: () => forgotPassword(email.trim()), onError: gate.release });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (gate.claim()) mut.mutate();
      }}
    >
      <CardHeader>
        <CardTitle>パスワードの再設定</CardTitle>
        <CardDescription>登録済みのメールアドレス宛に、再設定用のリンクをお送りします。</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {mut.isSuccess ? (
          <Alert variant="success">
            <AlertTitle>送信しました</AlertTitle>
            <AlertDescription>
              入力されたメールアドレスが登録済みであれば、再設定用のメールが届きます。
              数分経っても届かない場合は迷惑メールフォルダをご確認ください。
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <FormAlert error={mut.error} />
            <Field label="メールアドレス" htmlFor="forgot-email" required>
              <Input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </Field>
            <SubmitBar
              submitLabel="再設定メールを送る"
              pending={mut.isPending}
              disabled={email.trim().length === 0}
              onCancel={onBack}
              cancelLabel="戻る"
            />
          </>
        )}
        {mut.isSuccess ? (
          <Button type="button" variant="outline" className="w-full" onClick={onBack}>
            ログイン画面へ戻る
          </Button>
        ) : null}
      </CardContent>
    </form>
  );
}
